import { FirebaseError } from "firebase/app";
import { TranslationKeys } from "../i18n/translations";

// Map Firebase Auth error codes to translation keys
export function getFirebaseAuthError(
  error: unknown,
  t: TranslationKeys,
  context: "login" | "register",
): string {
  // Handle custom validation errors first
  if (error instanceof Error) {
    const message = error.message;

    // Custom validation error codes
    if (message === "EMAIL_MISMATCH") {
      return t.register.errors.emailMismatch;
    }
    if (message === "PASSWORD_MISMATCH") {
      return t.register.errors.passwordMismatch;
    }
    if (message === "PASSWORD_TOO_SHORT") {
      return t.register.errors.passwordLength;
    }
    if (message === "WEAK_PASSWORD") {
      return t.register.errors.passwordRequirements;
    }
    if (message === "EMAIL_NOT_VERIFIED") {
      return t.login.errors.emailNotVerified;
    }
  }

  if (!(error instanceof FirebaseError)) {
    if (error instanceof Error) {
      return error.message;
    }
    return context === "login"
      ? t.login.errors.authError
      : t.register.errors.registrationError;
  }

  const errorCode = error.code;

  // Login-specific errors
  if (context === "login") {
    switch (errorCode) {
      case "auth/invalid-email":
      case "auth/wrong-password":
      case "auth/invalid-credential":
        return t.login.errors.wrongCredentials;
      case "auth/user-not-found":
        return t.login.errors.userNotFound;
      case "auth/user-disabled":
        return t.login.errors.userDisabled;
      case "auth/too-many-requests":
        return t.login.errors.tooManyRequests;
      case "auth/network-request-failed":
        return t.login.errors.networkError;
      default:
        return t.login.errors.authError;
    }
  }

  // Register-specific errors
  switch (errorCode) {
    case "auth/email-already-in-use":
      return t.register.errors.emailAlreadyInUse;
    case "auth/invalid-email":
      return t.register.errors.invalidEmail;
    case "auth/weak-password":
      return t.register.errors.weakPassword;
    case "auth/network-request-failed":
      return t.register.errors.networkError;
    case "permission-denied":
    case "auth/operation-not-allowed":
      return t.register.errors.permissionDenied;
    default:
      return t.register.errors.registrationError;
  }
}

// Map Firebase/backend function errors to translated messages
export function getApiError(
  error: unknown,
  t: TranslationKeys,
  defaultMessage?: string,
): string {
  if (error instanceof FirebaseError) {
    const code = error.code;

    // Common Firebase function errors
    switch (code) {
      case "functions/unauthenticated":
        return t.login.errors.authError;
      case "functions/permission-denied":
        return t.register.errors.permissionDenied;
      case "functions/not-found":
        return t.common.error;
      case "functions/unavailable":
      case "functions/deadline-exceeded":
        return t.register.errors.networkError;
      default:
        break;
    }
  }

  if (error instanceof Error) {
    // Check for specific messages from backend
    const msg = error.message.toLowerCase();
    if (msg.includes("permission") || msg.includes("autorizzato")) {
      return t.register.errors.permissionDenied;
    }
    if (msg.includes("network") || msg.includes("connessione")) {
      return t.register.errors.networkError;
    }
    return error.message;
  }

  return defaultMessage || t.common.error;
}
